import type { ChartTimeframe } from "./types";

/** Spec §8.3 default history per timeframe. */
export const DEFAULT_HISTORY_DAYS: Record<ChartTimeframe, number> = {
  "15m": 7,
  "1h":  30,
  "4h":  90,
  "1d":  365,
};

export const TIMEFRAMES: readonly ChartTimeframe[] = ["15m", "1h", "4h", "1d"];

export const TIMEFRAME_SECONDS: Record<ChartTimeframe, number> = {
  "15m": 15 * 60,
  "1h":  60 * 60,
  "4h":  4 * 60 * 60,
  "1d":  24 * 60 * 60,
};

/**
 * Default `since` for a chart query as an ISO-8601 UTC string,
 * computed from the current time.
 */
export function computeSinceISO(
  timeframe: ChartTimeframe,
  rangeExtensionDays: number,
  now: Date = new Date(),
): string {
  const days = DEFAULT_HISTORY_DAYS[timeframe] + Math.max(0, rangeExtensionDays);
  const raw = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
  // Floor to the bucket boundary: a raw `now - N days` is unique per
  // millisecond, which churned the React Query key on every mount AND fed
  // a unique `since` into the server's ETag — the 304 design never fired.
  // Bucket-aligned, every client in the same bucket shares one cache key
  // and one ETag input.
  return floorBucket(raw, timeframe).toISOString();
}

/**
 * Floor a Date to the start of its bucket aligned to the 2024-01-01 UTC
 * epoch (matching Postgres date_bin used server-side).
 */
export function floorBucket(dt: Date, timeframe: ChartTimeframe): Date {
  const EPOCH = Date.UTC(2024, 0, 1);
  const intervalMs = TIMEFRAME_SECONDS[timeframe] * 1000;
  const since = dt.getTime() - EPOCH;
  const floored = Math.floor(since / intervalMs) * intervalMs;
  return new Date(EPOCH + floored);
}

/**
 * Read the chart's timezone from the calendar feature's
 * localStorage key (spec §8.5 — single source of truth across the app).
 */
export function resolveChartTz(): string {
  try {
    const raw = window.localStorage.getItem("calendar.tz");
    if (raw && typeof raw === "string" && raw.length > 0) return raw;
  } catch {
    /* ignore */
  }
  return "America/New_York";
}
